import React from 'react';
import { Stack, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EuroIcon from '@mui/icons-material/Euro';
import { MortgageExternalLead } from '@tassomutuo-backoffice/types';
import ExternalLeadCardShell from './ExternalLeadCardShell';
import { formatNumberWithThousandsSeparator } from '../../../../utils/formatMoney';

interface IProps {
  lead: MortgageExternalLead;
  onOpenDetails: (id: string) => void;
}

const buildFullName = (lead: MortgageExternalLead) =>
  [lead.name, lead.lastName].filter(Boolean).join(' ').trim();

const buildSource = (lead: MortgageExternalLead) => {
  const parts: string[] = [];
  if (lead.agenzia_euroansa_da_cui_arriva_la_lead) {
    parts.push(`Agenzia: ${lead.agenzia_euroansa_da_cui_arriva_la_lead}`);
  }
  if (lead.consulente_richiesto_pagina) {
    parts.push(`Consulente: ${lead.consulente_richiesto_pagina}`);
  }
  if (lead.segnalatore) {
    parts.push(`Segnalatore: ${lead.segnalatore}`);
  }
  if (lead.comune) {
    parts.push(`${lead.comune}${lead.provincia ? ` (${lead.provincia})` : ''}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
};

const buildProductInfo = (lead: MortgageExternalLead) => {
  const bank = lead.product?.nomebanca;
  const productName = lead.product?.nomeprodotto;
  const importo = lead.estimatorData?.importo;
  const durata = lead.estimatorData?.durata;
  const tipoTasso = lead.estimatorData?.tipoTasso;
  const finalita = lead.estimatorData?.finalita;
  const hasImporto =
    importo !== undefined && importo !== null && importo !== '';

  if (!bank && !hasImporto && !durata && !finalita) return null;

  const rowOne: string[] = [];
  if (bank) rowOne.push(bank);
  if (productName) rowOne.push(productName);

  const rowTwo: string[] = [];
  if (durata) rowTwo.push(`${durata} anni`);
  if (tipoTasso) rowTwo.push(tipoTasso);
  if (finalita) rowTwo.push(finalita);

  return (
    <Stack spacing={0.5}>
      {rowOne.length > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AccountBalanceIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={500}>
            {rowOne.join(' · ')}
          </Typography>
        </Stack>
      )}
      <Stack direction="row" spacing={0.5} alignItems="center">
        {hasImporto && (
          <>
            <EuroIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={500}>
              {formatNumberWithThousandsSeparator(String(importo))}
            </Typography>
          </>
        )}
        {rowTwo.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {hasImporto ? ' · ' : ''}
            {rowTwo.join(' · ')}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const ExternalLeadCardMortgage = ({ lead, onOpenDetails }: IProps) => {
  return (
    <ExternalLeadCardShell
      status={lead.status}
      quality={lead.quality}
      createdAt={lead.createdAt}
      fullName={buildFullName(lead)}
      email={lead.email}
      prefix={lead.prefix}
      phone={lead.phone}
      source={buildSource(lead)}
      productInfo={buildProductInfo(lead)}
      onOpenDetails={() => onOpenDetails(lead.id)}
    />
  );
};

export default ExternalLeadCardMortgage;
